import React from 'react';
import { Box, Typography, Button, Grid } from '@mui/material';
import { styled } from '@mui/material/styles';
import { CheckCircle } from 'lucide-react';
import { useNavigate } from 'react-router-dom';

interface ProposedSolutionProps {
  title: string;
  description: string;
  cost: string;
  timeframe: string;
  solutionId: string;
  onViewDetails?: (solutionId: string) => void;
}

const SolutionContainer = styled(Box)(({ theme }) => ({
  background: 'rgba(26, 26, 46, 0.6)',
  backdropFilter: 'blur(10px)',
  borderRadius: theme.spacing(2),
  border: '1px solid rgba(138, 43, 226, 0.2)',
  padding: theme.spacing(3),
  marginBottom: theme.spacing(4),
  position: 'relative',
  overflow: 'hidden',
  '&::before': {
    content: '""',
    position: 'absolute',
    top: 0,
    left: 0,
    width: '4px',
    height: '100%',
    background: 'linear-gradient(to bottom, #00CC99, #8A2BE2)',
  },
}));

const ViewDetailsButton = styled(Button)(({ theme }) => ({
  background: 'linear-gradient(45deg, #8A2BE2, #00CC99)',
  color: '#fff',
  padding: theme.spacing(1, 3),
  borderRadius: theme.spacing(1),
  textTransform: 'none',
  fontWeight: 600,
  boxShadow: '0 4px 20px rgba(138, 43, 226, 0.3)',
  transition: 'all 0.3s ease',
  '&:hover': {
    background: 'linear-gradient(45deg, #8A2BE2, #00CC99)',
    boxShadow: '0 6px 25px rgba(138, 43, 226, 0.5)',
    transform: 'translateY(-2px)',
  },
}));

const ProposedSolution: React.FC<ProposedSolutionProps> = ({
  title,
  description,
  cost,
  timeframe,
  solutionId,
  onViewDetails,
}) => {
  const navigate = useNavigate();

  const handleViewDetails = () => {
    if (onViewDetails) {
      onViewDetails(solutionId);
    } else {
      navigate(`/solution-detail/${solutionId}`);
    }
  };

  return (
    <Box sx={{ mb: 4 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
        <CheckCircle size={20} color="#00CC99" style={{ marginRight: 8 }} />
        <Typography variant="h5" sx={{ color: '#fff' }}>
          Proposed Solution
        </Typography>
      </Box>

      <SolutionContainer>
        <Typography variant="h6" sx={{ color: '#fff', mb: 2 }}>
          {title}
        </Typography>
        <Typography variant="body2" sx={{ color: 'rgba(255, 255, 255, 0.7)', mb: 3 }}>
          {description}
        </Typography>

        <Grid container spacing={4} sx={{ mb: 3 }}>
          <Grid item xs={12} sm={6}>
            <Typography variant="subtitle2" sx={{ color: 'rgba(255, 255, 255, 0.6)', mb: 1 }}>
              Estimated Cost:
            </Typography>
            <Typography variant="h6" sx={{ color: '#00CC99' }}>
              {cost}
            </Typography>
          </Grid>
          <Grid item xs={12} sm={6}>
            <Typography variant="subtitle2" sx={{ color: 'rgba(255, 255, 255, 0.6)', mb: 1 }}>
              Timeframe:
            </Typography>
            <Typography variant="h6" sx={{ color: '#fff' }}>
              {timeframe}
            </Typography>
          </Grid>
        </Grid>

        <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
          <ViewDetailsButton onClick={handleViewDetails}>
            View Solution Details
          </ViewDetailsButton>
        </Box>
      </SolutionContainer>
    </Box>
  );
};

export default ProposedSolution; 